import {
    Badge,
    Box,
    Button,
    ButtonGroup,
    Container,
    Divider,
    Flex,
    GridItem,
    Heading,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Spinner,
    Td,
    Text,
    Tr,
    Tooltip,
    useColorModeValue,
    useDisclosure,
    IconButton,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { FaFileCsv, FaFileExport } from "react-icons/fa";
import { Form, Icon, Searchable, Table } from "components";
import { API, Enums, Mask, Moment } from "config";
import { useEffect, useState } from "react";
import useOrdersProductList from "../../../hooks/useOrdersProductList";
import useProductOrderDetail from "../../../hooks/useProductOrderDetail";

function FilterLabel({ children }) {
    const color = useColorModeValue("gray.400", "gray.500");
    return (
        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={color} mb={1}>
            {children}
        </Text>
    );
}

function ProductOrderDetailModal({ product, filters, isOpen, onClose }) {
    const detail = useProductOrderDetail(product?.produto, {
        type: filters.type,
        status: filters.status,
        client: filters.client,
        distributorId: filters.distributorId,
        orderBy: filters.orderBy,
        period: filters.period,
    });

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize="md" fontWeight="bold">{product?.produto}</Text>
                    <Text fontSize="xs" color="gray.500" fontWeight="normal">
                        {product?.total_pedidos} pedidos · {product?.total_quantidade} unidades · R$ {Mask.price(product?.total_valor)}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {detail.isLoading && (
                        <Flex justify="center" py={8}><Spinner /></Flex>
                    )}
                    {!detail.isLoading && detail.data?.data?.length === 0 && (
                        <Text color="gray.500" textAlign="center" py={8}>Nenhum pedido encontrado.</Text>
                    )}
                    {detail.data?.data?.length > 0 && (
                        <Table
                            columns={[
                                { children: "Pedido" },
                                { children: "Tipo" },
                                { children: "Status" },
                                { children: "Cliente" },
                                { children: "Qtd", isNumeric: true },
                                { children: "Valor Unit.", isNumeric: true },
                                { children: "Total", isNumeric: true },
                                { children: "Pendência" },
                                { children: "Data" },
                            ]}
                        >
                            {detail.data.data.map((item, i) => {
                                const typeInfo = Enums.OrderType.data(item.order_type);
                                const statusInfo = Enums.OrderStatus.data(item.order_status);
                                return (
                                    <Tr key={i}>
                                        <Td fontSize="xs" fontFamily="mono">{item.order_cod}</Td>
                                        <Td>
                                            <Badge colorScheme={typeInfo.colorScheme} fontSize="xs">
                                                {typeInfo.label}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={statusInfo.colorScheme} fontSize="xs">
                                                {statusInfo.label}
                                            </Badge>
                                        </Td>
                                        <Td fontSize="xs" maxW="160px">
                                            <Tooltip label={item.customer} isDisabled={!item.customer} placement="top" hasArrow openDelay={300}>
                                                <Text noOfLines={1}>{item.customer ?? "—"}</Text>
                                            </Tooltip>
                                        </Td>
                                        <Td isNumeric>{item.quantity}</Td>
                                        <Td isNumeric>R$ {Mask.price(item.price_und)}</Td>
                                        <Td isNumeric>R$ {Mask.price(item.amount)}</Td>
                                        <Td>
                                            {item.is_pending
                                                ? <Badge colorScheme="orange" fontSize="xs">PDN</Badge>
                                                : "—"}
                                        </Td>
                                        <Td fontSize="xs">{Moment(item.date).format("DD/MM/YY HH:mm")}</Td>
                                    </Tr>
                                );
                            })}
                        </Table>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default function Product() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedProduct, setSelectedProduct] = useState(null);

    const initial = [Moment().subtract(3, "months").toDate(), Moment().toDate()];

    const [period, setPeriod]       = useState(initial);
    const [page, setPage]           = useState(1);
    const [term, setTerm]           = useState("");
    const [type, setType]           = useState("");
    const [status, setStatus]       = useState("");
    const [client, setClient]       = useState("");
    const [distributorId, setDistributor] = useState("all");
    const [orderBy, setOrderBy]     = useState("created_at");
    const [filterKey, setFilterKey]             = useState(0);
    const [isExporting, setIsExporting]         = useState(false);
    const [isExportingDetail, setIsExportingDetail] = useState(false);

    const validPeriod =
        period && period[0] instanceof Date && period[1] instanceof Date
            ? period
            : null;

    const filters = { type, status, client, distributorId, orderBy, period: validPeriod };

    const orders = useOrdersProductList(
        page, term, type, status, client, distributorId, orderBy, validPeriod
    );

    const hasActiveFilters = !!(term || type || status || client || distributorId !== "all");

    const clearFilters = () => {
        setTerm("");
        setType("");
        setStatus("");
        setClient("");
        setDistributor("all");
        setOrderBy("created_at");
        setPeriod(initial);
        setPage(1);
        setFilterKey(k => k + 1);
    };

    const handleRowClick = (item) => {
        setSelectedProduct(item);
        onOpen();
    };

    const handleExportCsv = async () => {
        if (!validPeriod) return;
        setIsExporting(true);
        try {
            const startFormatted = Moment(validPeriod[0]).format("YYYY-MM-DD");
            const endFormatted   = Moment(validPeriod[1]).format("YYYY-MM-DD");

            const url =
                `/reports/products?export=1` +
                `&period[]=${startFormatted}` +
                `&period[]=${endFormatted}` +
                (term          ? `&term=${term}`                    : "") +
                (type          ? `&type=${type}`                    : "") +
                (status        ? `&status=${status}`                : "") +
                (client        ? `&client=${client}`                : "") +
                (distributorId ? `&distributor_id=${distributorId}` : "") +
                (orderBy       ? `&order_by=${orderBy}`             : "");

            const response = await API.get(url);
            const rows = response?.data?.data ?? [];
            if (!rows.length) return;

            const header = ["Produto", "Total Pedidos", "Quantidade Vendida", "Total Valor"];
            const csvContent = [
                header.join(";"),
                ...rows.map((item) =>
                    [item.produto, item.total_pedidos, item.total_quantidade, item.total_valor].join(";")
                ),
            ].join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "relatorio_produtos.csv";
            link.click();
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportDetailedCsv = async () => {
        if (!validPeriod) return;
        setIsExportingDetail(true);
        try {
            const startFormatted = Moment(validPeriod[0]).format("YYYY-MM-DD");
            const endFormatted   = Moment(validPeriod[1]).format("YYYY-MM-DD");

            const url =
                `/reports/products/detail?period[]=${startFormatted}` +
                `&period[]=${endFormatted}` +
                (type          ? `&type=${type}`                    : "") +
                (status        ? `&status=${status}`                : "") +
                (client        ? `&client=${client}`                : "") +
                (distributorId ? `&distributor_id=${distributorId}` : "") +
                (orderBy       ? `&order_by=${orderBy}`             : "");

            const response = await API.get(url);
            const rows = response?.data?.data ?? [];
            if (!rows.length) return;

            const typeLabels = { 1: "Consultor", 2: "Núcleo", 3: "Adesão", 4: "Voucher" };
            const statusLabels = {
                1: "Rascunho", 2: "Finalizado", 3: "Aprovado", 4: "Processando",
                5: "Cancelado", 6: "Montagem", 7: "Cotação", 8: "Ag. Envio",
                9: "Em Trânsito", 10: "Parcial", 11: "Ag. Retirada", 12: "Análise", 13: "Fiscal",
            };

            const header = ["Produto", "Pedido", "Tipo", "Status", "Cliente", "Qtd", "Valor Unit.", "Total", "Pendência", "Data"];
            const csvContent = [
                header.join(";"),
                ...rows.map((item) => [
                    item.produto,
                    item.order_cod,
                    typeLabels[item.order_type]     ?? item.order_type,
                    statusLabels[item.order_status] ?? item.order_status,
                    item.customer ?? "",
                    item.quantity,
                    item.price_und,
                    item.amount,
                    item.is_pending ? "Sim" : "Não",
                    item.date ? Moment(item.date).format("DD/MM/YYYY HH:mm") : "",
                ].join(";"))
            ].join("\n");

            const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "relatorio_produtos_detalhado.csv";
            link.click();
        } finally {
            setIsExportingDetail(false);
        }
    };

    useEffect(() => { setPage(1); }, [term, type, status, distributorId, client, orderBy]);

    // ── Cores do painel (mesmo padrão dos pedidos) ──────────────────
    const filterBg      = useColorModeValue("gray.50",  "gray.700");
    const filterBorder  = useColorModeValue("gray.100", "gray.600");
    const filterLabelC  = useColorModeValue("gray.400", "gray.500");
    const dividerC      = useColorModeValue("gray.200", "gray.600");
    const clearBg       = useColorModeValue("red.50",   "rgba(254,178,178,0.1)");
    const clearBorder   = useColorModeValue("red.200",  "rgba(254,178,178,0.2)");
    const clearHoverBg  = useColorModeValue("red.100",  "rgba(254,178,178,0.18)");
    const clearActiveBg = useColorModeValue("red.200",  "rgba(254,178,178,0.25)");

    return (
        <Container maxW="90%" mt={10}>

            <Flex justifyContent="space-between" alignItems="center" mb={6}>
                <Heading>Relatório De Vendas Por Produto</Heading>
                <ButtonGroup size="xs">
                    <IconButton
                        icon={<RepeatIcon />}
                        aria-label="Recarregar"
                        isLoading={orders.isFetching}
                        onClick={orders.refetch}
                    />
                    <IconButton
                        icon={<FaFileCsv />}
                        aria-label="Exportar CSV resumido"
                        onClick={handleExportCsv}
                        isLoading={isExporting}
                        isDisabled={orders.isFetching || !orders.data?.data?.length}
                        colorScheme="blue"
                    />
                    <IconButton
                        icon={<FaFileExport />}
                        aria-label="Exportar CSV detalhado (por pedido)"
                        onClick={handleExportDetailedCsv}
                        isLoading={isExportingDetail}
                        isDisabled={orders.isFetching || !orders.data?.data?.length}
                        colorScheme="teal"
                    />
                </ButtonGroup>
            </Flex>

            {/* ── Painel de filtros ─────────────────────────────────── */}
            <Box
                bg={filterBg}
                border="1px solid"
                borderColor={filterBorder}
                borderRadius="xl"
                p={4}
                mb={4}
            >
                {/* Header */}
                <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                        <Icon name="search" boxSize={3.5} color={filterLabelC} />
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={filterLabelC}>
                            Filtros
                        </Text>
                        {hasActiveFilters && (
                            <Box w="6px" h="6px" borderRadius="full" bg="#008AAC" flexShrink={0} />
                        )}
                    </HStack>

                    {hasActiveFilters && (
                        <Button
                            size="sm"
                            onClick={clearFilters}
                            borderRadius="full"
                            px={4}
                            h="28px"
                            fontSize="xs"
                            fontWeight="semibold"
                            bg={clearBg}
                            color="red.500"
                            border="1px solid"
                            borderColor={clearBorder}
                            _hover={{ bg: clearHoverBg, borderColor: "red.300" }}
                            _active={{ bg: clearActiveBg }}
                            leftIcon={<Icon name="close" boxSize={3} />}
                            transition="all 0.15s"
                        >
                            Limpar filtros
                        </Button>
                    )}
                </Flex>

                {/* Busca */}
                <SimpleGrid columns={1} mb={4}>
                    <Searchable value={term} setValue={setTerm} placeholder="Pesquisar por nome do produto..." />
                </SimpleGrid>

                {/* Escopo */}
                <FilterLabel>Escopo</FilterLabel>
                <SimpleGrid key={`scope-${filterKey}`} columns={{ base: 2, md: 4 }} gap={2} mb={4}>
                    <Form.Select
                        onChange={e => setDistributor(e.target.value)}
                        placeholder="Selecione..."
                        options={[
                            { label: "Matriz", value: "matriz" },
                            { label: "Todos", value: "all" },
                        ]}
                    />
                    <Form.Select
                        onChange={e => setClient(e.target.value)}
                        placeholder="Núcleos/Consultores"
                        options={[
                            { label: "Núcleos/Consultores", value: "" },
                            { label: "Pedidos de Consultores", value: "consultant" },
                            { label: "Pedidos de Núcleos", value: "distributor" },
                        ]}
                    />
                    <Form.Select
                        onChange={e => setType(e.target.value)}
                        placeholder="Todos os tipos"
                        options={[
                            { label: "Todos os tipos", value: "" },
                            { label: "Pedidos de Produtos", value: "products" },
                            { label: "Pedidos de Voucher", value: "vouchers" },
                            { label: "Pedidos de Adesão", value: "adhesion" },
                            { label: "Pendências", value: "pending" },
                        ]}
                    />
                    <Form.Select
                        onChange={e => setStatus(e.target.value)}
                        placeholder="Todos os status"
                        options={[
                            { label: "Todos os status", value: "" },
                            { label: "Aprovado", value: Enums.OrderStatus.APPROVED },
                            { label: "Cancelado", value: Enums.OrderStatus.CANCELED },
                            { label: "Em Análise", value: Enums.OrderStatus.ANALYZING },
                            { label: "Montagem", value: Enums.OrderStatus.MONTAGE },
                            { label: "Cotação", value: Enums.OrderStatus.QUOTATION },
                            { label: "Nota Fiscal", value: Enums.OrderStatus.FISCAL },
                            { label: "Aguardando retirada", value: Enums.OrderStatus.WITHDRAWAL },
                            { label: "Aguardando envio", value: Enums.OrderStatus.WAITING_SHIPPING },
                            { label: "Em Trânsito", value: Enums.OrderStatus.SHIPPING },
                            { label: "Concluído", value: Enums.OrderStatus.DONE },
                        ]}
                    />
                </SimpleGrid>

                {/* Ordenação */}
                <FilterLabel>Ordenação</FilterLabel>
                <Flex key={`ord-${filterKey}`} gap={2} align="center" flexWrap={{ base: "wrap", md: "nowrap" }}>
                    <Box flex={1} minW="180px">
                        <Form.Select
                            onChange={e => setOrderBy(e.target.value)}
                            placeholder="Ordenar por"
                            options={[
                                { label: "Data do pedido", value: "created_at" },
                                { label: "Data de aprovação", value: "approved_at" },
                            ]}
                        />
                    </Box>
                    <Divider orientation="vertical" h="32px" borderColor={dividerC} flexShrink={0} display={{ base: "none", md: "block" }} />
                    <Box flexShrink={0}>
                        <Form.Interval initial={initial} onSelect={setPeriod} />
                    </Box>
                </Flex>
            </Box>

            <Table
                columns={[
                    { children: "Produto" },
                    { children: "Total Pedidos", isNumeric: true },
                    { children: "Quantidade Vendida", isNumeric: true },
                    { children: "Total Valor", isNumeric: true },
                ]}
                setPage={setPage}
                meta={orders.data?.meta}
            >
                {orders.data?.data?.map((item, index) => (
                    <Tr
                        key={index}
                        cursor="pointer"
                        _hover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
                        onClick={() => handleRowClick(item)}
                    >
                        <Td>{item.produto}</Td>
                        <Td isNumeric>{item.total_pedidos}</Td>
                        <Td isNumeric>{item.total_quantidade}</Td>
                        <Td isNumeric>R$ {Mask.price(item.total_valor)}</Td>
                    </Tr>
                ))}
            </Table>

            {selectedProduct && (
                <ProductOrderDetailModal
                    product={selectedProduct}
                    filters={filters}
                    isOpen={isOpen}
                    onClose={onClose}
                />
            )}
        </Container>
    );
}
